// src/pages/finance/InvoiceList.js

import PaymentForm from './PaymentForm';
import MassPaymentForm from './MassPaymentForm';
import ReceiptOptionsDialog from '../../components/finance/ReceiptOptionsDialog';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  MenuItem,
  Chip,
  Stack,
  FormControl,
  Select,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  Menu,
  Link,
  Zoom,
  Tabs,
  Tab,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  PlayArrow as ExecuteIcon,
  Download as DownloadIcon,
  MonetizationOn as MonetizationOnIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ErrorOutline as ErrorOutlineIcon,
  Mail as EmailIcon,
  PictureAsPdf as PdfIcon,
  ReceiptLong as TicketIcon,
  Visibility as VisibilityIcon,
  LocalOffer as DiscountIcon,
  MoreVert as MoreVertIcon,
  Error as ErrorIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarExport
} from '@mui/x-data-grid';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const parseLocalDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  const dateStr = String(dateVal);
  const cleanStr = dateStr.split('T')[0].split(' ')[0];
  const parts = cleanStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateVal);
};


const PaymentPromiseDialog = ({ open, onClose, onSubmit, invoice, currencySymbol }) => {
  const [promiseDate, setPromiseDate] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPromiseDate(null);
      setError('');
    }
  }, [open]);

  const handleSubmit = () => {
    if (!promiseDate) {
      setError('Debe seleccionar una fecha');
      return;
    }

    try {
      const formattedDate = format(promiseDate, 'yyyy-MM-dd');
      onSubmit(invoice.id, formattedDate);
    } catch (e) {
      setError('La fecha seleccionada no es válida');
    }
  };

  const existingPromise = invoice?.payment_promise_date ? String(invoice.payment_promise_date).split('T')[0] : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Registrar Promesa de Pago</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {existingPromise && (
            <Alert severity="info">
              Esta factura actualmente ya posee una promesa fijada para el <strong>{existingPromise}</strong>. Al guardar una nueva fecha, se actualizará la extensión.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            <strong>Cliente:</strong> {invoice?.client_name}
            <br />
            <strong>Factura #:</strong> {invoice?.invoice_number}
            <br />
            <strong>Monto:</strong> {currencySymbol}{invoice?.amount}
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              label="Fecha Promesa de Pago"
              value={promiseDate}
              onChange={setPromiseDate}
              format="dd/MM/yyyy"
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              minDate={new Date()}
            />
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Registrar Promesa
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ApplyDiscountDialog = ({ open, onClose, onSubmit, invoice, currencySymbol }) => {
  const [discountAmount, setDiscountAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Reset fields when opened/closed
  useEffect(() => {
    if (open) {
      setDiscountAmount('');
      setReason('');
      setError('');
    }
  }, [open]);

  // Calculate remaining pending balance
  const originalAmount = parseFloat(invoice?.amount || 0);
  const paidAmount = parseFloat(invoice?.paid_amount || invoice?.total_paid || 0);
  const pendingAmount = Math.max(0, originalAmount - paidAmount);

  const handleSubmit = () => {
    const parsedAmt = parseFloat(discountAmount);
    if (isNaN(parsedAmt) || parsedAmt <= 0) {
      setError('El monto de descuento debe ser mayor a cero');
      return;
    }
    if (parsedAmt > pendingAmount) {
      setError(`El descuento no puede exceder el saldo pendiente de ${currencySymbol} ${pendingAmount.toFixed(2)}`);
      return;
    }
    if (!reason.trim()) {
      setError('Debe especificar un motivo para el descuento');
      return;
    }

    onSubmit(invoice.id, parsedAmt, reason);
  };

  const previewTotal = Math.max(0, originalAmount - parseFloat(discountAmount || 0));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Aplicar Descuento a Factura</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}>
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Factura:</strong> {invoice?.invoice_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Cliente:</strong> {invoice?.client_name}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Total Original:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{currencySymbol} {originalAmount.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Total Pagado:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{currencySymbol} {paidAmount.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#dc2626' }}>Saldo Pendiente:</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#dc2626' }}>{currencySymbol} {pendingAmount.toFixed(2)}</Typography>
            </Box>
          </Box>

          <TextField
            label={`Monto de Descuento (${currencySymbol})`}
            type="number"
            value={discountAmount}
            onChange={(e) => setDiscountAmount(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ step: '0.01', min: '0.01' }}
          />

          <TextField
            label="Motivo del Descuento"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Especificar el motivo"
          />

          {discountAmount && parseFloat(discountAmount) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a8a' }}>Nuevo Total Factura:</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a8a' }}>{currencySymbol} {previewTotal.toFixed(2)}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}>
          Aplicar Descuento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InvoiceItemSection = ({ items, setItems, catalogServices, currencySymbol }) => {
  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddItem = () => {
    setItems(prev => [...prev, { description: '', quantity: 1, amount: '' }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      alert('La factura debe contener al menos 1 ítem.');
      return;
    }
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const totalCalculated = items.reduce((sum, item) => {
    const q = parseFloat(item.quantity || 1);
    const p = parseFloat(item.amount || 0);
    return sum + (q * p);
  }, 0);

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
          Conceptos / Ítems de la Factura
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
        >
          Agregar Ítem
        </Button>
      </Box>

      {items.map((item, index) => {
        const qty = parseFloat(item.quantity || 1);
        const unitPrice = parseFloat(item.amount || 0);
        const subtotal = (qty * unitPrice).toFixed(2);

        return (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 1.5,
              border: '1px solid #cbd5e1',
              borderRadius: '12px',
              backgroundColor: '#f8fafc'
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={catalogServices}
                  getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name || opt.description || '')}
                  value={item.description || ''}
                  onInputChange={(e, val) => handleItemChange(index, 'description', val || '')}
                  onChange={(e, val) => {
                    if (typeof val === 'object' && val !== null) {
                      setItems(prev => {
                        const next = [...prev];
                        next[index] = {
                          ...next[index],
                          description: val.name || val.description || '',
                          amount: val.price || val.amount || ''
                        };
                        return next;
                      });
                    } else if (typeof val === 'string') {
                      handleItemChange(index, 'description', val);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Ítem #${index + 1} - Concepto`}
                      placeholder="Ej. Servicio de Internet, Mantenimiento..."
                      size="small"
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#ffffff' } }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <TextField
                  label="Cant."
                  type="number"
                  size="small"
                  inputProps={{ min: 1, step: 1 }}
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#ffffff' } }}
                />
              </Grid>

              <Grid item xs={5} sm={2.5}>
                <TextField
                  label={`P. Unit (${currencySymbol})`}
                  type="number"
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                  value={item.amount}
                  onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#ffffff' } }}
                />
              </Grid>

              <Grid item xs={3} sm={1.5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveItem(index)}
                  disabled={items.length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pr: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                Subtotal: {currencySymbol} {subtotal}
              </Typography>
            </Box>
          </Paper>
        );
      })}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#e0e7ff', borderRadius: '10px', border: '1px solid #c7d2fe' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#3730a3' }}>
          Monto Total Calculado:
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#3730a3' }}>
          {currencySymbol} {totalCalculated.toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
};

const CreateInvoiceDialog = ({ open, onClose, onSubmit, clients, currencySymbol }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [docType, setDocType] = useState('recibo');
  const [emissionDate, setEmissionDate] = useState(new Date());
  const [dueDate, setDueDate] = useState(new Date());
  const [items, setItems] = useState([
    { description: '', quantity: 1, amount: '' }
  ]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');

  // Fetch catalog services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('/api/finance/billing-services', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCatalogServices(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error fetching catalog services:', err);
      }
    };
    if (open) {
      fetchServices();
    }
  }, [open]);

  // Auto-fill client details and Item 1 when client is selected
  useEffect(() => {
    if (!selectedClient) {
      setItems([{ description: '', quantity: 1, amount: '' }]);
      setDocType('recibo');
      return;
    }

    const loadDetails = async () => {
      setLoadingDetails(true);
      setError('');
      try {
        const response = await fetch(`/api/clients/${selectedClient.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const clientDetails = await response.json();
          const currentMonth = new Date().toLocaleString('es-ES', { month: 'long' });
          const capMonth = currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1);
          const planDesc = `Plan ${clientDetails.plan_name || ''} de Internet - ${capMonth}`;
          const planPrice = clientDetails.plan_price || '';

          setItems([
            { description: planDesc, quantity: 1, amount: planPrice }
          ]);
          setDocType(clientDetails.document_type || 'recibo');
          if (clientDetails.due_date) {
            setDueDate(parseLocalDate(clientDetails.due_date));
          }
        }
      } catch (err) {
        console.error('Error auto-filling client details:', err);
      } finally {
        setLoadingDetails(false);
      }
    };

    loadDetails();
  }, [selectedClient]);

  const handleSubmit = () => {
    if (!selectedClient) {
      setError('Debe seleccionar un cliente');
      return;
    }
    if (!dueDate) {
      setError('Debe seleccionar una fecha de vencimiento');
      return;
    }

    const validItems = items.filter(it => it.description.trim() && parseFloat(it.amount || 0) > 0);
    if (validItems.length === 0) {
      setError('Debe ingresar al menos un ítem válido con concepto y precio mayor a 0');
      return;
    }

    const totalCalculated = validItems.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity || 1) * parseFloat(item.amount || 0));
    }, 0);

    const mainDescription = validItems[0].description;

    onSubmit({
      client_id: selectedClient.id,
      amount: Number(totalCalculated.toFixed(2)),
      emission_date: format(emissionDate, 'yyyy-MM-dd'),
      due_date: format(dueDate, 'yyyy-MM-dd'),
      description: mainDescription,
      document_type: docType,
      items: validItems.map(it => ({
        description: it.description,
        quantity: parseFloat(it.quantity || 1),
        amount: parseFloat(it.amount || 0),
        unit_price: parseFloat(it.amount || 0),
        total: parseFloat((parseFloat(it.quantity || 1) * parseFloat(it.amount || 0)).toFixed(2))
      }))
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', borderBottom: '1px solid rgba(0,0,0,0.06)', pb: 2 }}>
        Crear Nueva Factura / Recibo
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box className="space-y-5 py-4" sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>
              {error}
            </Alert>
          )}

          <Autocomplete
            options={clients}
            getOptionLabel={(option) => option.fullName || ''}
            value={selectedClient}
            onChange={(event, newValue) => setSelectedClient(newValue)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Cliente" 
                variant="outlined" 
                required 
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingDetails ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '12px' }
            }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
                  Tipo de Comprobante
                </Typography>
                <Select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="recibo">Recibo</MenuItem>
                  <MenuItem value="boleta">Boleta</MenuItem>
                  <MenuItem value="factura">Factura</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
                  Fecha de Emisión
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DatePicker
                    value={emissionDate}
                    onChange={setEmissionDate}
                    slotProps={{ textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
                  Fecha de Vencimiento
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DatePicker
                    value={dueDate}
                    onChange={setDueDate}
                    slotProps={{ textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
          </Grid>

          <InvoiceItemSection
            items={items}
            setItems={setItems}
            catalogServices={catalogServices}
            currencySymbol={currencySymbol}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Button onClick={onClose} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          sx={{ 
            borderRadius: '10px', 
            textTransform: 'none', 
            fontWeight: 700, 
            px: 3,
            backgroundColor: '#6366f1',
            '&:hover': { backgroundColor: '#4f46e5' }
          }}
        >
          Crear Factura
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditInvoiceDialog = ({ open, onClose, onSubmit, invoice, currencySymbol }) => {
  const [dueDate, setDueDate] = useState(invoice ? (invoice.due_date ? new Date(invoice.due_date) : new Date()) : new Date());
  const [notes, setNotes] = useState(invoice ? (invoice.notes || '') : '');
  const [items, setItems] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch catalog services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('/api/finance/billing-services', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCatalogServices(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error fetching catalog services:', err);
      }
    };
    if (open) {
      fetchServices();
    }
  }, [open]);

  useEffect(() => {
    if (invoice) {
      setDueDate(invoice.due_date ? new Date(invoice.due_date) : new Date());
      setNotes(invoice.notes || '');

      let loadedItems = [];
      if (invoice.items) {
        try {
          loadedItems = typeof invoice.items === 'string' ? JSON.parse(invoice.items) : invoice.items;
        } catch (e) {}
      }

      if (Array.isArray(loadedItems) && loadedItems.length > 0) {
        setItems(loadedItems.map(it => ({
          description: it.description || '',
          quantity: it.quantity || 1,
          amount: it.amount || it.unit_price || it.price || ''
        })));
      } else {
        setItems([
          {
            description: invoice.description || 'Servicio de Internet',
            quantity: 1,
            amount: invoice.amount || ''
          }
        ]);
      }
    }
  }, [invoice]);

  const paidAmount = parseFloat(invoice?.paid_amount || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const validItems = items.filter(it => it.description.trim() && parseFloat(it.amount || 0) > 0);
    if (validItems.length === 0) {
      alert('Debe ingresar al menos un ítem válido con concepto y precio mayor a 0');
      return;
    }

    const calculatedTotal = validItems.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity || 1) * parseFloat(item.amount || 0));
    }, 0);

    if (paidAmount > 0 && calculatedTotal < paidAmount) {
      alert(`El nuevo monto (${currencySymbol} ${calculatedTotal.toFixed(2)}) no puede ser menor que la suma de abonos ya registrados (${currencySymbol} ${paidAmount.toFixed(2)}).`);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(invoice.id, {
        due_date: dueDate,
        notes: notes,
        description: validItems[0].description,
        items: validItems.map(it => ({
          description: it.description,
          quantity: parseFloat(it.quantity || 1),
          amount: parseFloat(it.amount || 0),
          unit_price: parseFloat(it.amount || 0),
          total: parseFloat((parseFloat(it.quantity || 1) * parseFloat(it.amount || 0)).toFixed(2))
        }))
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!invoice) return null;

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const isOverdueExtended = invoice.status === 'overdue' && dueDate >= todayStart;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Editar Factura #{invoice.invoice_number}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Cliente: {invoice.client_name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {isOverdueExtended && (
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              Extender la fecha de vencimiento a una fecha futura actualizará el estado de la factura a <strong>Pendiente</strong> y re-evaluará la reconexión automática en MikroTik.
            </Alert>
          )}

          {paidAmount > 0 && (
            <Alert severity="warning" sx={{ borderRadius: '12px' }}>
              Esta factura tiene abonos registrados por <strong>{currencySymbol} {paidAmount.toFixed(2)}</strong>. El nuevo monto total no puede ser inferior a esta cifra.
            </Alert>
          )}

          <FormControl fullWidth>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
              Fecha de Vencimiento
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                slotProps={{ textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
              />
            </LocalizationProvider>
          </FormControl>

          <InvoiceItemSection
            items={items}
            setItems={setItems}
            catalogServices={catalogServices}
            currencySymbol={currencySymbol}
          />

          <FormControl fullWidth>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
              Observaciones / Notas Internas
            </Typography>
            <TextField
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              placeholder="Notas u observaciones sobre este ajuste..."
              InputProps={{ sx: { borderRadius: '12px' } }}
            />
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Button onClick={onClose} disabled={submitting} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={submitting}
          variant="contained" 
          color="primary"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ 
            borderRadius: '10px', 
            textTransform: 'none', 
            fontWeight: 700, 
            px: 3,
            backgroundColor: '#6366f1',
            '&:hover': { backgroundColor: '#4f46e5' }
          }}
        >
          {submitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const colors = {
  grey: '#64748b',
};

const StatCard = ({ title, value, icon: Icon, color, subtitle, delay = 0 }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay * 100}ms` }}>
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.03)',
        borderLeft: `4px solid ${color}`,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px 0 rgba(31, 38, 135, 0.08)',
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.68rem' }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, color: '#1e293b', fontSize: '1.25rem' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: colors.grey, display: 'block', marginTop: '2px', fontWeight: 500, fontSize: '0.7rem' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}15`,
              color: color,
              '& svg': { fontSize: 20 },
              flexShrink: 0
            }}
          >
            {Icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Zoom>
);

const InvoiceList = () => {
  const { hasPermission, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [successPaymentData, setSuccessPaymentData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuInvoice, setMenuInvoice] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [promiseDialogOpen, setPromiseDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [selectedInvoiceForDiscount, setSelectedInvoiceForDiscount] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openPaymentForm, setOpenPaymentForm] = useState(false);
  const [openMassPaymentForm, setOpenMassPaymentForm] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('$');

  // Estados para filtros avanzados y reportes de facturas
  const [dateType, setDateType] = useState('due_date');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [plansList, setPlansList] = useState([]);
  const [zonesList, setZonesList] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Estado para creación de factura manual
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [fetchingClientsList, setFetchingClientsList] = useState(false);

  // Estado para acciones masivas
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

  // Estado para modal de visualización de captura/voucher de pago
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  // Estado para modal de visualización de Comprobante SUNAT PDF (A4 vs Ticket)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerData, setPdfViewerData] = useState({
    sunatNumber: '',
    pdfUrlA4: '',
    pdfUrlTicket: '',
    currentFormat: 'a4'
  });

  const handleOpenPdfModal = (url, sunatNumber = '') => {
    if (!url) return;
    let a4Url = url;
    let ticketUrl = url;

    if (url.includes('/pdf/a4/')) {
      ticketUrl = url.replace('/pdf/a4/', '/pdf/ticket/');
    } else if (url.includes('/pdf/ticket/')) {
      a4Url = url.replace('/pdf/ticket/', '/pdf/a4/');
    }

    setPdfViewerData({
      sunatNumber: sunatNumber || 'Comprobante SUNAT',
      pdfUrlA4: a4Url,
      pdfUrlTicket: ticketUrl,
      currentFormat: 'a4'
    });
    setPdfViewerOpen(true);
  };

  // Estado para cliente seleccionado (filtro por cliente)
  const [clientFilter, setClientFilter] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);

  // Estado para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Cargar lista de planes, zonas y configuración de moneda al montar
  useEffect(() => {
    const fetchPlansZonesAndSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const plansRes = await fetch('/api/plans', { headers });
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setPlansList(plansData);
        }
        
        const zonesRes = await fetch('/api/zones', { headers });
        if (zonesRes.ok) {
          const zonesData = await zonesRes.json();
          setZonesList(zonesData);
        }

        const settingsRes = await fetch('/api/finance/settings', { headers });
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          if (settingsData && settingsData.currency) {
            const getSymbol = (code) => {
              switch (code) {
                case 'PEN': return 'S/';
                case 'DOP': return 'RD$';
                case 'USD':
                case 'MXN':
                case 'COP':
                  return '$';
                default: return '$';
              }
            };
            setCurrencySymbol(getSymbol(settingsData.currency));
          }
        }
      } catch (err) {
        console.error('Error loading plans/zones/settings for filters:', err);
      }
    };
    
    fetchPlansZonesAndSettings();
  }, []);

  useEffect(() => {
    // Obtener el clientId de la URL si existe
    const searchParams = new URLSearchParams(location.search);
    const clientId = searchParams.get('clientId');
    const tabParam = searchParams.get('tab');

    if (clientId) {
      setClientFilter(clientId);
      fetchClientInfo(clientId);
    } else {
      setClientFilter(null);
      setClientInfo(null);
    }

    if (tabParam === 'pending') {
      setActiveTab(1);
    } else if (tabParam === 'overdue') {
      setActiveTab(2);
    } else if (tabParam === 'paid') {
      setActiveTab(3);
    } else {
      setActiveTab(0);
    }

    fetchInvoices(clientId);
    fetchPaymentMethods();
  }, [location.search, dateRange, dateType, paymentMethodFilter, planFilter, documentTypeFilter, zoneFilter]);

  // Función para obtener información del cliente
  const fetchClientInfo = async (clientId) => {
    if (!clientId) return;

    try {
      const response = await fetch(`/api/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setClientInfo(data);
    } catch (error) {
      console.error('Error fetching client info:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch('/api/payment-methods', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleDownloadInvoice = (invoiceId, format = 'normal') => {
    // Obtener el token
    const token = localStorage.getItem('token');

    // Construir la URL de impresión
    const printUrl = `/api/invoices/${invoiceId}/download?format=${format}&inline=true${format === 'ticket' ? '&html=true' : ''}&t=${Date.now()}${token ? `&token=${token}` : ''}`;

    // Crear iframe para impresión directa
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = printUrl;

    document.body.appendChild(iframe);

    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Error triggering print in iframe:', e);
        window.open(printUrl, '_blank');
      }

      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 5000);
    };
  };

  const handleSendEmailDirectly = async (invoiceId, format = 'ticket') => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ format })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar el correo');
      }
      alert('Correo enviado exitosamente');
    } catch (error) {
      console.error('Error al enviar correo:', error);
      alert(error.message || 'Error al enviar el correo');
    }
  };

  const handleMassDownload = async () => {
    if (selectedRows.length === 0) return;

    try {
      // Mostrar mensaje de carga
      setActionInProgress(true);

      // Obtener el token
      const token = localStorage.getItem('token');

      // Crear un elemento para la descarga
      const link = document.createElement('a');
      link.style.display = 'none';
      document.body.appendChild(link);

      // Configuración de la solicitud
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ invoiceIds: selectedRows })
      };

      // Realizar la solicitud para la descarga en bulk
      const response = await fetch('/api/invoices/bulk-download', requestOptions);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      // Convertir la respuesta a blob
      const blob = await response.blob();

      // Crear URL para descargar
      const url = window.URL.createObjectURL(blob);
      link.href = url;

      // Obtener nombre del archivo del header Content-Disposition o usar uno por defecto
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
      link.download = filenameMatch ? filenameMatch[1] : 'facturas.zip';

      // Descargar
      link.click();

      // Limpiar
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Notificar al usuario
      alert(`Se han descargado ${selectedRows.length} facturas en un archivo ZIP.`);

    } catch (error) {
      console.error('Error descargando facturas en bulk:', error);
      alert(`Error al descargar las facturas: ${error.message}`);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta factura?')) {
      return;
    }

    try {
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        // Actualizar la lista de facturas optimísticamente
        setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
        setFilteredInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
        fetchInvoices(clientFilter);
      } else {
        const data = await response.json();
        alert(data.message || 'Error al eliminar la factura');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Error al eliminar la factura');
    }
  };

  const handleOpenPaymentForm = (invoice) => {
    console.log('Datos de factura enviados al formulario:', invoice); // Para depurar
    setSelectedInvoice(invoice);
    setOpenPaymentForm(true);
  };

  const handlePaymentSubmit = async (paymentData) => {
    try {
      const payload = {
        ...paymentData
      };
      
      if (selectedInvoice.isAdvancePayment) {
        payload.client_id = selectedInvoice.client_id;
      } else {
        payload.invoice_id = selectedInvoice.id;
      }

      const response = await fetch('/api/payments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al registrar el pago');
      }

      // Cerrar formulario
      setOpenPaymentForm(false);
      
      const isAdvance = selectedInvoice.isAdvancePayment;
      const invoiceIdVal = selectedInvoice.id;
      const invoiceNoVal = selectedInvoice.invoice_number || 'Pago Adelantado';
      const clientEmailVal = selectedInvoice.client_email || '';

      setSelectedInvoice(null);

      // Actualizar la lista de facturas para reflejar el nuevo estado
      fetchInvoices(clientFilter);
      
      // Si fue pago adelantado, actualizar la información del cliente filtrado
      if (isAdvance && clientFilter) {
        fetchClientInfo(clientFilter);
      }

      if (!isAdvance) {
        setSuccessPaymentData({
          id: invoiceIdVal,
          invoiceNumber: invoiceNoVal,
          amount: paymentData.amount,
          clientEmail: clientEmailVal
        });
        setReceiptDialogOpen(true);
      } else {
        alert('Pago adelantado registrado exitosamente');
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      alert(error.message || 'Error al registrar el pago');
    }
  };

  const handleMassPaymentSubmit = async (paymentData) => {
    try {
      setActionInProgress(true);

      const response = await fetch('/api/invoices/mass-action', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invoiceIds: selectedRows,
          action: 'register_payment',
          paymentMethod: paymentData.paymentMethod,
          paymentReference: paymentData.paymentReference,
          proofImageUrl: paymentData.proofImageUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al registrar el pago masivo');
      }

      setOpenMassPaymentForm(false);
      setSelectedRows([]);
      setSelectedAction('');
      setSelectedPaymentMethod('');
      fetchInvoices(clientFilter);
      alert('Pago masivo registrado exitosamente');
    } catch (error) {
      console.error('Error executing mass payment:', error);
      alert(error.message || 'Error al registrar el pago masivo');
    } finally {
      setActionInProgress(false);
    }
  };

  const fetchInvoices = async (clientId = null) => {
    try {
      // Construir los parámetros de consulta
      const params = new URLSearchParams();

      if (dateRange.startDate && dateRange.endDate) {
        params.append('startDate', format(dateRange.startDate, 'yyyy-MM-dd'));
        params.append('endDate', format(dateRange.endDate, 'yyyy-MM-dd'));
        params.append('dateType', dateType);
      }

      // Agregar clientId a los parámetros si existe
      if (clientId) {
        params.append('clientId', clientId);
      }

      if (paymentMethodFilter) {
        params.append('paymentMethodId', paymentMethodFilter);
      }

      if (planFilter) {
        params.append('planId', planFilter);
      }

      if (documentTypeFilter) {
        params.append('documentType', documentTypeFilter);
      }

      if (zoneFilter) {
        params.append('zoneId', zoneFilter);
      }

      // Evitar almacenamiento en caché del navegador o Service Worker
      params.append('_t', Date.now());

      const url = `/api/invoices${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await fetch(url, {
        cache: 'no-store',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Actualizar los estados con los datos recibidos
      const invoicesData = data.invoices || [];
      setInvoices(invoicesData);
      setFilteredInvoices(invoicesData);
      setTotalAmount(data.totalAmount || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      // Establecer valores por defecto en caso de error
      setInvoices([]);
      setFilteredInvoices([]);
      setTotalAmount(0);
      setLoading(false);
    }
  };

  // Función para limpiar el filtro de cliente
  const handleClearClientFilter = () => {
    // Actualizar URL sin el clientId
    navigate('/finance/invoices');

    // Limpiar estados relacionados con el filtro de cliente
    setClientFilter(null);
    setClientInfo(null);

    // Recargar facturas sin filtro
    fetchInvoices();
  };

  const handlePaymentPromise = async (invoiceId, promiseDate) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/payment-promise`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ promiseDate })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al registrar promesa de pago');

      // Actualizar inmediatamente en memoria las facturas para que el UI refleje la nueva fecha sin depender del caché
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, payment_promise_date: promiseDate, status: 'pending' } : inv));
      setFilteredInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, payment_promise_date: promiseDate, status: 'pending' } : inv));

      alert(data.message || 'Promesa de pago registrada exitosamente. El servicio ha sido activado.');
      fetchInvoices(clientFilter);
      if (clientFilter) fetchClientInfo(clientFilter);
      if (activeTab === 2) {
        setActiveTab(1);
      }
      setPromiseDialogOpen(false);
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Error registering payment promise:', error);
      alert(error.message || 'Error al registrar promesa de pago');
    }
  };

  const handleApplyDiscount = async (invoiceId, discountAmount, reason) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/apply-discount`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount_amount: discountAmount,
          reason
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al aplicar el descuento');

      alert(data.message || 'Descuento aplicado exitosamente');
      fetchInvoices(clientFilter);
      setDiscountDialogOpen(false);
      setSelectedInvoiceForDiscount(null);
    } catch (error) {
      console.error('Error applying discount:', error);
      alert('Error al aplicar el descuento: ' + error.message);
    }
  };

  const handleEmitBoletaSunat = async (invoiceId) => {
    try {
      const response = await fetch(`/api/finance/sunat/emit-boleta/${invoiceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al emitir comprobante SUNAT');
      
      const providerName = data.provider === 'lucode'
        ? 'LuCode / APISunat'
        : data.provider === 'factiliza'
          ? 'Factiliza API'
          : 'NubeFact';
      const emitNumber = data.sunat_number || data.number || 'Generada';
      const pdfLink = data.pdf_url || data.pdfUrl || data.sunat_pdf_url;

      alert(`¡Comprobante SUNAT emitido exitosamente vía ${providerName}! Número: ${emitNumber}`);
      fetchInvoices(clientFilter);
      if (pdfLink) {
        handleOpenPdfModal(pdfLink, emitNumber);
      }
    } catch (error) {
      console.error('Error emitting SUNAT Invoice:', error);
      alert('Error al emitir comprobante SUNAT: ' + error.message);
    }
  };

  const handleEditInvoiceSubmit = async (invoiceId, updateData) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al actualizar la factura');

      alert(data.message || 'Factura actualizada exitosamente');
      fetchInvoices(clientFilter);
      setEditDialogOpen(false);
      setSelectedInvoiceForEdit(null);
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('Error al actualizar la factura: ' + error.message);
      throw error;
    }
  };

  const handleOpenCreateInvoiceDialog = async () => {
    setCreateInvoiceOpen(true);
    setFetchingClientsList(true);
    try {
      const response = await fetch('/api/clients', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setFetchingClientsList(false);
    }
  };

  const handleCreateInvoiceSubmit = async (invoiceData) => {
    try {
      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invoiceData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear la factura');
      }

      setCreateInvoiceOpen(false);
      fetchInvoices(clientFilter);
      alert('Factura creada exitosamente');
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert(error.message || 'Error al crear la factura');
    }
  };

  // Función para manejar acciones masivas
  const executeMassAction = async () => {
    if (!selectedAction || selectedRows.length === 0) return;

    // Para acciones que requieren método de pago
    if (selectedAction === 'register_payment') {
      setOpenMassPaymentForm(true);
      return;
    }

    // Acción de descarga masiva
    if (selectedAction === 'download') {
      handleMassDownload();
      return;
    }

    // Mensajes de confirmación específicos para cada tipo de acción
    const confirmMessage = selectedAction === 'register_payment'
      ? `¿Está seguro de que desea registrar pagos para las ${selectedRows.length} facturas seleccionadas?`
      : selectedAction === 'delete'
        ? `¿Está seguro de que desea ELIMINAR las ${selectedRows.length} facturas seleccionadas? Esta acción no se puede deshacer.`
        : `¿Está seguro de que desea procesar las ${selectedRows.length} facturas seleccionadas?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Si es eliminación masiva
    if (selectedAction === 'delete') {
      try {
        setActionInProgress(true);
        let successCount = 0;
        let errorCount = 0;
        let successfulDeletes = [];

        for (const invoiceId of selectedRows) {
          try {
            const response = await fetch(`/api/invoices/${invoiceId}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (response.ok) {
              successCount++;
              successfulDeletes.push(invoiceId);
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
            console.error(`Error eliminando factura ${invoiceId}:`, err);
          }
        }

        // Limpiar selección y refrescar datos
        setSelectedRows([]);
        setSelectedAction('');
        if (successfulDeletes.length > 0) {
           setInvoices(prev => prev.filter(inv => !successfulDeletes.includes(inv.id)));
           setFilteredInvoices(prev => prev.filter(inv => !successfulDeletes.includes(inv.id)));
        }
        fetchInvoices(clientFilter);

        // Mostrar resultado
        if (errorCount === 0) {
          alert(`Se eliminaron exitosamente ${successCount} facturas.`);
        } else {
          alert(`Se eliminaron ${successCount} facturas. No se pudieron eliminar ${errorCount} facturas (posiblemente porque tienen pagos asociados).`);
        }
      } catch (error) {
        console.error('Error en eliminación masiva:', error);
        alert(error.message || 'Error al ejecutar la eliminación masiva');
      } finally {
        setActionInProgress(false);
      }
      return;
    }

    // Para registrar pagos (usar la API existente)
    try {
      setActionInProgress(true);

      const response = await fetch('/api/invoices/mass-action', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invoiceIds: selectedRows,
          action: selectedAction,
          paymentMethod: selectedPaymentMethod
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al ejecutar acción masiva');
      }

      // Limpiar selección y refrescar datos
      setSelectedRows([]);
      setSelectedAction('');
      setSelectedPaymentMethod('');
      fetchInvoices(clientFilter);
      alert('Acción completada exitosamente');
    } catch (error) {
      console.error('Error executing mass action:', error);
      alert(error.message || 'Error al ejecutar la acción masiva');
    } finally {
      setActionInProgress(false);
    }
  };

  // Barra de herramientas personalizada con acciones masivas de alta gama
  const CustomToolbar = () => {
    if (selectedRows.length > 0) {
      return (
        <GridToolbarContainer sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between', backgroundColor: '#f8fafc', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4f46e5', backgroundColor: 'rgba(99, 102, 241, 0.08)', px: 2, py: 0.8, borderRadius: '12px', width: { xs: '100%', sm: 'auto' }, textAlign: 'center' }}>
              {selectedRows.length} seleccionadas
            </Typography>

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <Select
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                displayEmpty
                sx={{ borderRadius: '12px', backgroundColor: 'white' }}
              >
                <MenuItem value="" disabled>Seleccionar Acción</MenuItem>
                <MenuItem value="register_payment">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MonetizationOnIcon sx={{ mr: 1, color: '#10b981' }} fontSize="small" />
                    Registrar Pago
                  </Box>
                </MenuItem>
                <MenuItem value="download">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DownloadIcon sx={{ mr: 1, color: '#0ea5e9' }} fontSize="small" />
                    Descargar PDF
                  </Box>
                </MenuItem>
                {(user?.role === 'admin' || hasPermission('roles.manage')) && (
                  <MenuItem value="delete">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DeleteIcon sx={{ mr: 1, color: '#ef4444' }} fontSize="small" />
                      Eliminar
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={executeMassAction}
              disabled={!selectedAction || actionInProgress}
              startIcon={actionInProgress ? <CircularProgress size={20} color="inherit" /> : <ExecuteIcon />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                backgroundColor: '#6366f1',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                '&:hover': { backgroundColor: '#4f46e5' },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {actionInProgress ? 'Ejecutando...' : 'Ejecutar'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PdfIcon />}
              onClick={handlePrintReport}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                color: '#6366f1',
                borderColor: '#e2e8f0',
                backgroundColor: 'white',
                '&:hover': { borderColor: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.04)' },
                height: '32px'
              }}
            >
              Imprimir Reporte
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                color: '#10b981',
                borderColor: '#e2e8f0',
                backgroundColor: 'white',
                '&:hover': { borderColor: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.04)' },
                height: '32px'
              }}
            >
              Exportar Excel
            </Button>
            <GridToolbarQuickFilter
              sx={{
                width: { xs: '100%', sm: 'auto' },
                '& .MuiInputBase-root': {
                  borderRadius: '12px',
                  backgroundColor: 'white',
                  px: 1,
                  border: '1px solid rgba(0,0,0,0.1)',
                  '&:before, &:after': { display: 'none' },
                  width: '100%'
                }
              }}
            />
          </Box>
        </GridToolbarContainer>
      );
    }

    return (
      <GridToolbarContainer sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PdfIcon />}
            onClick={handlePrintReport}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              px: { xs: 1, sm: 1.5 },
              color: '#6366f1',
              borderColor: '#e2e8f0',
              backgroundColor: 'white',
              '&:hover': { borderColor: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.04)' },
              height: '34px',
              flex: { xs: '1 1 45%', sm: 'none' },
              whiteSpace: 'nowrap'
            }}
          >
            Imprimir Reporte
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              px: { xs: 1, sm: 1.5 },
              color: '#10b981',
              borderColor: '#e2e8f0',
              backgroundColor: 'white',
              '&:hover': { borderColor: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.04)' },
              height: '34px',
              flex: { xs: '1 1 45%', sm: 'none' },
              whiteSpace: 'nowrap'
            }}
          >
            Exportar Excel
          </Button>
        </Box>
        <GridToolbarQuickFilter
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& .MuiInputBase-root': {
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              px: 1.5,
              py: 0.2,
              border: '1px solid rgba(0,0,0,0.06)',
              '&:before, &:after': { display: 'none' },
              width: '100%'
            }
          }}
        />
      </GridToolbarContainer>
    );
  };

  const getStatusChip = (status, promiseDate) => {
    if (promiseDate && status !== 'paid' && status !== 'cancelled' && status !== 'voided') {
      try {
        const parsed = parseLocalDate(promiseDate);
        if (parsed) {
          const dateStr = format(parsed, 'dd/MM/yyyy');
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isExpired = parsed < today;

          return (
            <Tooltip title={`Prórroga de pago ${isExpired ? 'vencida' : 'vigente'} hasta el ${dateStr}`} arrow>
              <Chip
                label={isExpired ? `Prórroga Vencida: ${dateStr}` : `Prórroga: ${dateStr}`}
                sx={{
                  background: isExpired ? 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)' : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                  color: isExpired ? '#be123c' : '#b45309',
                  border: `1px solid ${isExpired ? '#fecdd3' : '#fde68a'}`,
                  fontWeight: 600,
                  borderRadius: '8px',
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.78rem'
                  }
                }}
                size="small"
                icon={<CalendarIcon style={{ color: isExpired ? '#e11d48' : '#d97706', fontSize: '0.85rem' }} />}
              />
            </Tooltip>
          );
        }
      } catch (e) {
        console.error('Error parsing promiseDate:', e);
      }
    }

    switch (status) {
      case 'paid':
        return (
          <Chip 
            label="Pagada" 
            sx={{
              background: 'rgba(16, 185, 129, 0.08)',
              color: '#059669',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              fontWeight: 600,
              borderRadius: '8px',
            }}
            size="small" 
          />
        );
      case 'partially_paid':
        return (
          <Chip 
            label="Pago Parcial" 
            sx={{
              background: 'rgba(249, 115, 22, 0.08)',
              color: '#ea580c',
              border: '1px solid rgba(249, 115, 22, 0.2)',
              fontWeight: 600,
              borderRadius: '8px',
            }}
            size="small" 
          />
        );
      case 'pending':
        return (
          <Chip 
            label="Pendiente" 
            sx={{
              background: 'rgba(245, 158, 11, 0.08)',
              color: '#d97706',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              fontWeight: 600,
              borderRadius: '8px',
            }}
            size="small" 
          />
        );
      case 'overdue':
        return (
          <Chip 
            label="Vencida" 
            sx={{
              background: 'rgba(244, 63, 94, 0.08)',
              color: '#e11d48',
              border: '1px solid rgba(244, 63, 94, 0.2)',
              fontWeight: 600,
              borderRadius: '8px',
            }}
            size="small" 
          />
        );
      case 'voided':
        return (
          <Chip 
            label="Anulada" 
            sx={{
              background: 'rgba(100, 116, 139, 0.08)',
              color: '#64748b',
              border: '1px solid rgba(100, 116, 139, 0.2)',
              fontWeight: 600,
              borderRadius: '8px',
            }}
            size="small" 
          />
        );
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedRows([]); // Limpiar selección de filas al cambiar de pestaña

    const params = new URLSearchParams(location.search);
    if (newValue === 1) {
      params.set('tab', 'pending');
    } else if (newValue === 2) {
      params.set('tab', 'overdue');
    } else if (newValue === 3) {
      params.set('tab', 'paid');
    } else {
      params.delete('tab');
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Manejo de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper para obtener las facturas que deben incluirse en los reportes (PDF / Excel)
  const getReportInvoices = () => {
    const displayInvoices = getDisplayInvoices();
    if (selectedRows && selectedRows.length > 0) {
      return displayInvoices.filter(inv => selectedRows.includes(inv.id));
    }
    return displayInvoices;
  };

  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor, permite las ventanas emergentes para ver el reporte.');
      return;
    }

    const reportInvoices = getReportInvoices();

    const reportTotalInvoiced = reportInvoices.reduce((acc, inv) => acc + Number(inv.amount || 0), 0);
    const reportTotalPaid = reportInvoices.reduce((acc, inv) => acc + Number(inv.paid_amount || 0), 0);
    const reportTotalPending = reportInvoices.reduce((acc, inv) => acc + Math.max(0, Number(inv.amount || 0) - Number(inv.paid_amount || 0)), 0);
    const reportTotalOverdue = reportInvoices.filter(inv => inv.status === 'overdue').reduce((acc, inv) => acc + Math.max(0, Number(inv.amount || 0) - Number(inv.paid_amount || 0)), 0);

    const formattedTotalInvoiced = reportTotalInvoiced.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formattedTotalPaid = reportTotalPaid.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formattedTotalPending = reportTotalPending.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formattedTotalOverdue = reportTotalOverdue.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    let dateRangeText = 'Todos los registros';
    if (dateRange.startDate && dateRange.endDate) {
      const start = format(dateRange.startDate, 'dd/MM/yyyy');
      const end = format(dateRange.endDate, 'dd/MM/yyyy');
      let typeText = 'Vencimiento';
      if (dateType === 'emission_date') typeText = 'Emisión';
      if (dateType === 'payment_date') typeText = 'Pago';
      dateRangeText = `Filtrado por Fecha de ${typeText}: del ${start} al ${end}`;
    }

    const planText = planFilter ? (plansList.find(p => p.id === planFilter)?.name || 'Plan seleccionado') : 'Todos';
    const methodText = paymentMethodFilter ? (paymentMethods.find(m => m.id === paymentMethodFilter)?.name || 'Método seleccionado') : 'Todos';
    const zoneText = zoneFilter ? (zonesList.find(z => z.id === zoneFilter)?.name || 'Zona seleccionada') : 'Todas';
    const typeText = documentTypeFilter ? documentTypeFilter.toUpperCase() : 'Todos';
    
    let statusText = 'TODOS';
    if (selectedRows && selectedRows.length > 0) {
      statusText = `SELECCIONADOS (${selectedRows.length})`;
    } else if (activeTab === 1) {
      statusText = 'PENDIENTES';
    } else if (activeTab === 2) {
      statusText = 'VENCIDAS';
    } else if (activeTab === 3) {
      statusText = 'PAGADAS';
    }

    const tableRows = reportInvoices.map((inv, idx) => {
      const emissionDate = inv.created_at ? format(parseLocalDate(inv.created_at), 'dd/MM/yyyy') : '-';
      const dueDate = inv.due_date ? format(parseLocalDate(inv.due_date), 'dd/MM/yyyy') : '-';
      const paymentDate = inv.payment_date ? format(parseLocalDate(inv.payment_date), 'dd/MM/yyyy') : '-';
      const paid = Number(inv.paid_amount || 0);
      const pending = Math.max(0, Number(inv.amount || 0) - paid);
      
      let statusLabel = 'Pendiente';
      if (inv.status === 'paid') statusLabel = 'Pagada';
      if (inv.status === 'partially_paid') statusLabel = 'Pago Parcial';
      if (inv.status === 'overdue') statusLabel = 'Vencida';
      if (inv.status === 'voided') statusLabel = 'Anulada';

      return `
        <tr>
          <td>${idx + 1}</td>
          <td><strong>${inv.invoice_number}</strong><br><small style="color: #64748b; font-size: 0.75rem">${(inv.document_type || 'recibo').toUpperCase()}</small></td>
          <td>${inv.client_name}</td>
          <td>${inv.client_document_number || inv.client_phone || '-'}</td>
          <td><span class="badge badge-plan">${inv.plan_name || 'Sin Plan'}</span></td>
          <td>${emissionDate}</td>
          <td>${dueDate}</td>
          <td>${paymentDate}</td>
          <td>${inv.payment_method_name || '-'}</td>
          <td class="amount">${currencySymbol}${Number(inv.amount || 0).toFixed(2)}</td>
          <td class="amount" style="color: #059669">${currencySymbol}${paid.toFixed(2)}</td>
          <td class="amount" style="color: ${pending > 0 ? '#ea580c' : '#94a3b8'}">${currencySymbol}${pending.toFixed(2)}</td>
          <td><span class="badge badge-status status-${inv.status}">${statusLabel}</span></td>
        </tr>
      `;
    }).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Reporte de Facturación y Cobranzas - YinoWISP</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          
          body {
            font-family: 'Inter', sans-serif;
            color: #1e293b;
            background-color: #ffffff;
            margin: 0;
            padding: 40px;
            font-size: 12px;
            line-height: 1.5;
          }
          
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .logo-area h1 {
            margin: 0;
            font-size: 24px;
            font-weight: 800;
            background: linear-gradient(90deg, #0284c7 0%, #4f46e5 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          
          .logo-area p {
            margin: 5px 0 0 0;
            font-size: 11px;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 600;
          }
          
          .meta-area {
            text-align: right;
          }
          
          .meta-area p {
            margin: 3px 0;
            color: #475569;
          }
          
          .report-title {
            text-align: center;
            margin-bottom: 25px;
          }
          
          .report-title h2 {
            margin: 0;
            font-size: 18px;
            font-weight: 800;
            color: #0f172a;
            letter-spacing: -0.5px;
          }
          
          .report-title p {
            margin: 6px 0 0 0;
            color: #4f46e5;
            font-weight: 600;
            font-size: 12px;
          }
          
          .filters-box {
            background-color: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 15px 20px;
            margin-bottom: 30px;
          }
          
          .filters-box h3 {
            margin: 0 0 10px 0;
            font-size: 11px;
            color: #475569;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 700;
          }
          
          .filters-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px 20px;
          }
          
          .filter-item {
            font-size: 11px;
          }
          
          .filter-item strong {
            color: #475569;
          }
          
          .kpis-container {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 30px;
          }
          
          .kpi-card {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 12px 15px;
            background-color: #ffffff;
            border-left: 4px solid #64748b;
          }
          
          .kpi-card.invoiced { border-left-color: #6366f1; }
          .kpi-card.paid { border-left-color: #10b981; }
          .kpi-card.pending { border-left-color: #f59e0b; }
          .kpi-card.overdue { border-left-color: #f43f5e; }
          
          .kpi-title {
            font-size: 9px;
            text-transform: uppercase;
            color: #64748b;
            font-weight: 700;
            letter-spacing: 0.5px;
          }
          
          .kpi-value {
            font-size: 16px;
            font-weight: 700;
            color: #0f172a;
            margin-top: 4px;
            font-family: monospace;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          
          th {
            background-color: #f1f5f9;
            color: #475569;
            font-weight: 700;
            text-align: left;
            padding: 10px 8px;
            border-bottom: 2px solid #cbd5e1;
            font-size: 10px;
            text-transform: uppercase;
          }
          
          td {
            padding: 10px 8px;
            border-bottom: 1px solid #e2e8f0;
            vertical-align: middle;
          }
          
          tr:nth-child(even) {
            background-color: #f8fafc;
          }
          
          .amount {
            font-family: monospace;
            font-weight: 600;
            text-align: right;
            font-size: 11px;
          }
          
          th.amount {
            text-align: right;
          }
          
          .badge {
            display: inline-block;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
          }
          
          .badge-plan {
            background-color: #e0f2fe;
            color: #0369a1;
          }
          
          .badge-status.status-paid { background-color: #d1fae5; color: #065f46; }
          .badge-status.status-partially_paid { background-color: #ffedd5; color: #9a3412; }
          .badge-status.status-pending { background-color: #fef3c7; color: #92400e; }
          .badge-status.status-overdue { background-color: #ffe4e6; color: #991b1b; }
          .badge-status.status-voided { background-color: #f1f5f9; color: #475569; }
          
          .footer-print {
            text-align: center;
            color: #94a3b8;
            font-size: 10px;
            border-top: 1px solid #e2e8f0;
            padding-top: 20px;
            margin-top: 40px;
          }
          
          @media print {
            body {
              padding: 0;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-area">
            <h1>YinoWISP</h1>
            <p>Sistema de Gestión de ISP</p>
          </div>
          <div class="meta-area">
            <p><strong>Fecha Generado:</strong> ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}</p>
            <p><strong>Generado por:</strong> ${user?.username || 'Administrador'}</p>
          </div>
        </div>
        
        <div class="report-title">
          <h2>REPORTE DETALLADO DE FACTURACIÓN Y COBRANZAS</h2>
          <p>${dateRangeText}</p>
        </div>
        
        <div class="filters-box">
          <h3>Filtros Aplicados</h3>
          <div class="filters-grid">
            <div class="filter-item"><strong>Plan de Internet:</strong> ${planText}</div>
            <div class="filter-item"><strong>Método de Pago:</strong> ${methodText}</div>
            <div class="filter-item"><strong>Zona Geográfica:</strong> ${zoneText}</div>
            <div class="filter-item"><strong>Tipo de Comprobante:</strong> ${typeText}</div>
            <div class="filter-item"><strong>Estado del Comprobante:</strong> ${statusText}</div>
            <div class="filter-item"><strong>Total Comprobantes:</strong> ${reportInvoices.length}</div>
          </div>
        </div>
        
        <div class="kpis-container">
          <div class="kpi-card invoiced">
            <div class="kpi-title">Facturación Total</div>
            <div class="kpi-value">${currencySymbol}${formattedTotalInvoiced}</div>
          </div>
          <div class="kpi-card paid">
            <div class="kpi-title">Total Cobrado</div>
            <div class="kpi-value">${currencySymbol}${formattedTotalPaid}</div>
          </div>
          <div class="kpi-card pending">
            <div class="kpi-title">Monto Pendiente</div>
            <div class="kpi-value">${currencySymbol}${formattedTotalPending}</div>
          </div>
          <div class="kpi-card overdue">
            <div class="kpi-title">Total Vencido</div>
            <div class="kpi-value">${currencySymbol}${formattedTotalOverdue}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 3%">#</th>
              <th style="width: 12%">N° Comprobante</th>
              <th style="width: 18%">Cliente</th>
              <th style="width: 10%">Doc. Cliente</th>
              <th style="width: 10%">Plan</th>
              <th style="width: 8%">Emisión</th>
              <th style="width: 8%">Vencimiento</th>
              <th style="width: 8%">Fecha Pago</th>
              <th style="width: 9%">Medio Pago</th>
              <th class="amount" style="width: 8%">Total</th>
              <th class="amount" style="width: 8%">Cobrado</th>
              <th class="amount" style="width: 8%">Pendiente</th>
              <th style="width: 8%">Estado</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows || '<tr><td colspan="13" style="text-align: center; color: #94a3b8; padding: 30px;">No se encontraron registros de facturación bajo los filtros seleccionados.</td></tr>'}
          </tbody>
        </table>
        
        <div class="footer-print">
          Este documento es un reporte financiero oficial generado automáticamente por el Panel de Administración de YinoWISP.
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExportExcel = () => {
    const reportInvoices = getReportInvoices();

    const reportTotalInvoiced = reportInvoices.reduce((acc, inv) => acc + Number(inv.amount || 0), 0);
    const reportTotalPaid = reportInvoices.reduce((acc, inv) => acc + Number(inv.paid_amount || 0), 0);
    const reportTotalPending = reportInvoices.reduce((acc, inv) => acc + Math.max(0, Number(inv.amount || 0) - Number(inv.paid_amount || 0)), 0);
    const reportTotalOverdue = reportInvoices.filter(inv => inv.status === 'overdue').reduce((acc, inv) => acc + Math.max(0, Number(inv.amount || 0) - Number(inv.paid_amount || 0)), 0);

    const tableHeaders = [
      'N° Comprobante',
      'Cliente',
      'Doc. Cliente',
      'Plan',
      'Fecha Emisión',
      'Fecha Vencimiento',
      'Fecha Pago',
      'Medio Pago',
      'Total',
      'Cobrado',
      'Pendiente',
      'Días de Mora',
      'Estado',
      'Zona'
    ];

    const rowsHtml = reportInvoices.map((inv) => {
      const emissionDate = inv.created_at ? format(parseLocalDate(inv.created_at), 'dd/MM/yyyy') : '-';
      const dueDate = inv.due_date ? format(parseLocalDate(inv.due_date), 'dd/MM/yyyy') : '-';
      const paymentDate = inv.payment_date ? format(parseLocalDate(inv.payment_date), 'dd/MM/yyyy') : '-';
      const paid = Number(inv.paid_amount || 0);
      const pending = Math.max(0, Number(inv.amount || 0) - paid);
      
      let statusLabel = 'Pendiente';
      if (inv.status === 'paid') statusLabel = 'Pagada';
      if (inv.status === 'partially_paid') statusLabel = 'Pago Parcial';
      if (inv.status === 'overdue') statusLabel = 'Vencida';
      if (inv.status === 'voided') statusLabel = 'Anulada';

      // Calcular mora para el excel
      let daysOfMoraText = 'Al día';
      if (inv.status === 'pending' || inv.status === 'overdue') {
        const diffTime = new Date() - parseLocalDate(inv.due_date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 0) daysOfMoraText = `${diffDays} días`;
      } else if (inv.status === 'paid' && inv.payment_date) {
        const diffTime = parseLocalDate(inv.payment_date) - parseLocalDate(inv.due_date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 0) daysOfMoraText = `Pactado (+${diffDays}d)`;
      }

      return `
        <tr>
          <td class="text" style="mso-number-format:'\\@'; font-weight: bold;">${inv.invoice_number}</td>
          <td class="text">${inv.client_name}</td>
          <td class="text" style="mso-number-format:'\\@';">${inv.client_document_number || inv.client_phone || '-'}</td>
          <td class="text">${inv.plan_name || 'Sin Plan'}</td>
          <td class="date">${emissionDate}</td>
          <td class="date">${dueDate}</td>
          <td class="date">${paymentDate}</td>
          <td class="text">${inv.payment_method_name || '-'}</td>
          <td class="number">${Number(inv.amount || 0)}</td>
          <td class="number">${paid}</td>
          <td class="number">${pending}</td>
          <td class="text">${daysOfMoraText}</td>
          <td class="text">${statusLabel}</td>
          <td class="text">${inv.zone_name || 'Sin Zona'}</td>
        </tr>
      `;
    }).join('');

    const headersHtml = tableHeaders.map(h => `<th>${h}</th>`).join('');

    // Template HTML de Excel con estilos y soporte para números, alineación y colores
    const excelTemplate = `
      <html lang="es" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Reporte Facturación YinoWISP</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; }
          
          /* Estilo de la tabla corporativa */
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          
          /* Encabezado elegante en tonos azules e índigo de la marca */
          th { 
            background-color: #4f46e5; 
            color: #ffffff; 
            font-weight: bold; 
            font-size: 11pt;
            border: 1px solid #312e81; 
            padding: 10px 8px; 
            text-align: center; 
          }
          
          /* Celdas con bordes definidos y espaciado */
          td { 
            border: 1px solid #cbd5e1; 
            padding: 8px; 
            font-size: 10pt; 
            color: #1e293b;
          }
          
          /* Formatos específicos de Microsoft Excel */
          .text { text-align: left; }
          .number { text-align: right; mso-number-format: "0\\.00"; }
          .date { text-align: center; mso-number-format: "dd/mm/yyyy"; }
          
          /* Título del reporte */
          .title-area { 
            font-size: 16pt; 
            font-weight: bold; 
            color: #0f172a; 
            margin-bottom: 5px;
          }
          .subtitle-area { 
            font-size: 10pt; 
            color: #475569; 
            margin-bottom: 20px; 
          }
          
          /* KPIs de Resumen */
          .kpi-table { margin-bottom: 20px; }
          .kpi-label { background-color: #f1f5f9; font-weight: bold; }
          .kpi-val { text-align: right; font-weight: bold; mso-number-format: "0\\.00"; }
        </style>
      </head>
      <body>
        <div class="title-area">YinoWISP - Reporte de Facturación y Cobranzas</div>
        <div class="subtitle-area">Generado el: ${format(new Date(), 'dd/MM/yyyy HH:mm:ss')}</div>
        
        <!-- Tabla de KPIs de resumen en Excel -->
        <table class="kpi-table" style="width: 350px;">
          <tr>
            <td class="kpi-label" style="background-color: #e0e7ff;">Facturación Total:</td>
            <td class="kpi-val" style="color: #4f46e5; background-color: #e0e7ff;">${reportTotalInvoiced.toFixed(2)}</td>
          </tr>
          <tr>
            <td class="kpi-label" style="background-color: #d1fae5;">Total Cobrado:</td>
            <td class="kpi-val" style="color: #059669; background-color: #d1fae5;">${reportTotalPaid.toFixed(2)}</td>
          </tr>
          <tr>
            <td class="kpi-label" style="background-color: #fef3c7;">Monto Pendiente:</td>
            <td class="kpi-val" style="color: #d97706; background-color: #fef3c7;">${reportTotalPending.toFixed(2)}</td>
          </tr>
          <tr>
            <td class="kpi-label" style="background-color: #ffe4e6;">Total Vencido:</td>
            <td class="kpi-val" style="color: #e11d48; background-color: #ffe4e6;">${reportTotalOverdue.toFixed(2)}</td>
          </tr>
        </table>

        <!-- Tabla principal de datos -->
        <table>
          <thead>
            <tr>
              ${headersHtml}
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob(['\ufeff' + excelTemplate], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte_facturacion_${format(new Date(), 'yyyyMMdd_HHmmss')}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      field: 'invoice_number',
      headerName: 'N° Comprobante',
      width: 180,
      renderCell: (params) => {
        const docType = (params.row.document_type || 'recibo').toLowerCase();
        let typeBadgeColor = 'default';
        let typeLabel = 'RECIBO';
        
        if (docType === 'factura') {
          typeBadgeColor = 'primary';
          typeLabel = 'FACTURA';
        } else if (docType === 'boleta') {
          typeBadgeColor = 'secondary';
          typeLabel = 'BOLETA';
        }

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip 
              label={typeLabel} 
              color={typeBadgeColor} 
              size="small" 
              variant="outlined" 
              sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, borderRadius: '6px' }} 
            />
            <Link
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/finance/invoices/${params.row.id}`);
              }}
              sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                color: '#6366f1',
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {params.row.invoice_number}
            </Link>
          </Stack>
        );
      }
    },
    {
      field: 'client_name',
      headerName: 'Cliente',
      width: 170,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8} alignItems="center">
          <PersonIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
            {params.value}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'client_document_number',
      headerName: 'Doc. Cliente',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.8rem' }}>
          {params.value || params.row.client_phone || '-'}
        </Typography>
      )
    },
    {
      field: 'plan_name',
      headerName: 'Plan',
      width: 130,
      renderCell: (params) => {
        if (!params.value) return '';
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              borderRadius: '6px',
              backgroundColor: 'rgba(14, 165, 233, 0.08)',
              color: '#0284c7',
              fontWeight: 600,
              fontSize: '0.72rem'
            }}
          />
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Fecha Emisión',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return '';
        const formattedDate = format(parseLocalDate(params.value), 'dd/MM/yyyy');
        return (
          <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
            {formattedDate}
          </Typography>
        );
      }
    },
    {
      field: 'due_date',
      headerName: 'Fecha Vencimiento',
      width: 135,
      renderCell: (params) => {
        if (!params.value) return '';
        const formattedDate = format(parseLocalDate(params.value), 'dd/MM/yyyy');
        return (
          <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
            {formattedDate}
          </Typography>
        );
      }
    },
    {
      field: 'payment_promise_date',
      headerName: 'F. Prórroga',
      width: 130,
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.75rem' }}>
              -
            </Typography>
          );
        }
        try {
          const dateObj = parseLocalDate(params.value);
          const formattedDate = format(dateObj, 'dd/MM/yyyy');
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isExpired = dateObj < today;

          return (
            <Tooltip title={`Prórroga ${isExpired ? 'vencida' : 'vigente'} hasta el ${formattedDate}`} arrow>
              <Chip
                label={formattedDate}
                size="small"
                sx={{
                  backgroundColor: isExpired ? '#fff1f2' : '#fef3c7',
                  color: isExpired ? '#be123c' : '#92400e',
                  border: `1px solid ${isExpired ? '#fecdd3' : '#fde68a'}`,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  borderRadius: '6px'
                }}
              />
            </Tooltip>
          );
        } catch (e) {
          return String(params.value);
        }
      }
    },
    {
      field: 'payment_date',
      headerName: 'Fecha Pago',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return (
          <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.75rem' }}>
            Sin pago
          </Typography>
        );
        const formattedDate = format(parseLocalDate(params.value), 'dd/MM/yyyy');
        return (
          <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600 }}>
            {formattedDate}
          </Typography>
        );
      }
    },
    {
      field: 'payment_method_name',
      headerName: 'Método Pago',
      width: 130,
      renderCell: (params) => {
        if (!params.value) return '-';
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              borderRadius: '6px',
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              color: '#4f46e5',
              fontWeight: 600,
              fontSize: '0.72rem'
            }}
          />
        );
      }
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 100,
      type: 'number',
      renderCell: (params) => {
        const value = params.value ? Number(params.value) : 0;
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
            {currencySymbol}{value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      }
    },
    {
      field: 'paid_amount',
      headerName: 'Cobrado',
      width: 100,
      type: 'number',
      renderCell: (params) => {
        const value = params.value ? Number(params.value) : 0;
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#059669' }}>
            {currencySymbol}{value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      }
    },
    {
      field: 'pending_amount',
      headerName: 'Pendiente',
      width: 100,
      type: 'number',
      renderCell: (params) => {
        const amount = params.row.amount ? Number(params.row.amount) : 0;
        const paid = params.row.paid_amount ? Number(params.row.paid_amount) : 0;
        const pending = Math.max(0, amount - paid);
        
        if (pending === 0) {
          return (
            <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>
              Liquidado
            </Typography>
          );
        }
        
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: pending > 0 ? '#ea580c' : '#94a3b8' }}>
            {currencySymbol}{pending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      }
    },
    {
      field: 'days_overdue',
      headerName: 'Días de Mora',
      width: 110,
      renderCell: (params) => {
        const status = params.row.status;
        const dueDate = parseLocalDate(params.row.due_date);
        const paymentDate = params.row.payment_date ? parseLocalDate(params.row.payment_date) : null;
        
        if (status === 'pending' || status === 'overdue') {
          const diffTime = new Date() - dueDate;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > 0) {
            return (
              <Typography variant="body2" sx={{ color: '#e11d48', fontWeight: 700 }}>
                {diffDays} días
              </Typography>
            );
          }
          return (
            <Typography variant="body2" sx={{ color: '#059669', fontWeight: 500, fontSize: '0.75rem' }}>
              Al día
            </Typography>
          );
        }
        
        if (status === 'paid' && paymentDate) {
          const diffTime = paymentDate - dueDate;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > 0) {
            return (
              <Typography variant="body2" sx={{ color: '#b45309', fontWeight: 500, fontSize: '0.75rem' }}>
                Pactado (+{diffDays}d)
              </Typography>
            );
          }
          return (
            <Typography variant="body2" sx={{ color: '#059669', fontWeight: 500, fontSize: '0.75rem' }}>
              Puntual
            </Typography>
          );
        }
        
        return '';
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 175,
      renderCell: (params) => getStatusChip(params.row.status, params.row.payment_promise_date)
    },
    {
      field: 'zone_name',
      headerName: 'Zona',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Sin Zona'}
          size="small"
          sx={{
            borderRadius: '6px',
            backgroundColor: '#f1f5f9',
            color: '#475569',
            fontWeight: 500,
            fontSize: '0.72rem'
          }}
        />
      )
    },
    {
      field: 'sunat_status',
      headerName: 'SUNAT',
      width: 155,
      renderCell: (params) => {
        const status = params.row.sunat_status;
        const pdfUrl = params.row.sunat_pdf_url;
        const sunatNum = params.row.sunat_number;

        if (status === 'ACEPTADO') {
          return (
            <Tooltip title={`Boleta Emitida: ${sunatNum || ''} (Clic para abrir PDF)`}>
              <Chip
                icon={<CheckCircleIcon style={{ fontSize: 14, color: '#059669' }} />}
                label={sunatNum || 'Boleta SUNAT'}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (pdfUrl) handleOpenPdfModal(pdfUrl, sunatNum);
                }}
                sx={{
                  backgroundColor: '#ecfdf5',
                  color: '#047857',
                  borderColor: '#a7f3d0',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  cursor: pdfUrl ? 'pointer' : 'default'
                }}
              />
            </Tooltip>
          );
        } else if (status === 'ERROR') {
          return (
            <Tooltip title={params.row.sunat_response_message || 'Error al emitir boleta SUNAT'}>
              <Chip
                icon={<ErrorIcon style={{ fontSize: 14, color: '#dc2626' }} />}
                label="Error SUNAT"
                size="small"
                sx={{
                  backgroundColor: '#fef2f2',
                  color: '#b91c1c',
                  fontWeight: 700,
                  fontSize: '0.72rem'
                }}
              />
            </Tooltip>
          );
        }
        return (
          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', fontSize: '0.72rem' }}>
            Sin Boleta
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      renderCell: (params) => {
        return (
          <Tooltip title="Acciones">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setMenuInvoice(params.row);
                setAnchorEl(e.currentTarget);
              }}
              sx={{
                color: '#475569',
                backgroundColor: 'rgba(71, 85, 105, 0.06)',
                '&:hover': { backgroundColor: 'rgba(71, 85, 105, 0.12)' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  // Helper para filtrar en memoria según la pestaña activa
  const getDisplayInvoices = () => {
    switch (activeTab) {
      case 1: // Pendientes
        return filteredInvoices.filter(inv => inv.status === 'pending' || inv.status === 'partially_paid');
      case 2: // Vencidas
        return filteredInvoices.filter(inv => inv.status === 'overdue');
      case 3: // Pagadas
        return filteredInvoices.filter(inv => inv.status === 'paid');
      case 0: // Todas
      default:
        return filteredInvoices;
    }
  };

  // Contadores para las pestañas
  const countAll = filteredInvoices.length;
  const countPending = filteredInvoices.filter(inv => inv.status === 'pending' || inv.status === 'partially_paid').length;
  const countOverdue = filteredInvoices.filter(inv => inv.status === 'overdue').length;
  const countPaid = filteredInvoices.filter(inv => inv.status === 'paid').length;

  // Cálculos dinámicos para el panel de estadísticas de alta fidelidad
  const totalInvoiced = filteredInvoices.reduce((acc, inv) => acc + Number(inv.amount || 0), 0);
  const totalPaid = filteredInvoices.filter(inv => inv.status === 'paid').reduce((acc, inv) => acc + Number(inv.amount || 0), 0);
  const totalPending = filteredInvoices.filter(inv => inv.status === 'pending').reduce((acc, inv) => acc + Number(inv.amount || 0), 0);
  const totalOverdue = filteredInvoices.filter(inv => inv.status === 'overdue').reduce((acc, inv) => acc + Number(inv.amount || 0), 0);

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1, sm: 1.5, md: 2 } }}>
      {/* Cabecera Premium */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Lista de Facturas
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.2, fontWeight: 500, display: 'block' }}>
            Visualice, filtre y gestione los comprobantes de pago de los abonados.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={fetchingClientsList ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleOpenCreateInvoiceDialog}
            disabled={fetchingClientsList}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              height: '36px',
              fontSize: '0.875rem',
              backgroundColor: '#6366f1',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#4f46e5' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Nueva Factura
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              height: '36px',
              fontSize: '0.875rem',
              color: '#475569',
              borderColor: '#cbd5e1',
              '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#94a3b8' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {showAdvancedFilters ? 'Ocultar Filtros' : 'Filtros Avanzados'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PaymentIcon />}
            onClick={() => navigate('/finance/payment-reports')}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              height: '36px',
              fontSize: '0.875rem',
              color: '#6366f1',
              borderColor: '#6366f1',
              backgroundColor: 'white',
              '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.08)' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Historial de Pagos
          </Button>
          <Box sx={{
            p: 0.5,
            display: 'flex',
            flexDirection: 'row',
            width: { xs: '100%', sm: 'auto' },
            alignItems: 'center',
            gap: 1
          }}>
            <FormControl size="small" sx={{ minWidth: 135 }}>
              <Select
                value={dateType}
                onChange={(e) => setDateType(e.target.value)}
                sx={{ borderRadius: '8px', height: '36px', backgroundColor: 'white', fontSize: '0.8125rem', fontWeight: 600, color: '#334155' }}
              >
                <MenuItem value="due_date">F. Vencimiento</MenuItem>
                <MenuItem value="emission_date">F. Emisión</MenuItem>
                <MenuItem value="payment_date">F. Pago</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
              <DatePicker
                label="Inicio"
                value={dateRange.startDate}
                onChange={(date) => setDateRange(prev => ({ ...prev, startDate: date }))}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px', backgroundColor: 'white', fontSize: '0.8125rem' }, width: '125px' }
                  }
                }}
              />
              <DatePicker
                label="Fin"
                value={dateRange.endDate}
                onChange={(date) => setDateRange(prev => ({ ...prev, endDate: date }))}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px', backgroundColor: 'white', fontSize: '0.8125rem' }, width: '125px' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </Box>

      {/* Mostrar chip cuando hay filtro de cliente */}
      {clientInfo && (
        <Alert
          severity="info"
          sx={{ 
            mb: 1.5, 
            borderRadius: '12px', 
            border: '1px solid rgba(2, 132, 199, 0.15)', 
            backgroundColor: 'rgba(2, 132, 199, 0.03)',
            '& .MuiAlert-message': { width: '100%' }
          }}
          action={
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mt: { xs: 1, sm: 0 }, alignSelf: { xs: 'stretch', sm: 'center' } }}>
              <Button
                color="info"
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedInvoice({
                    isAdvancePayment: true,
                    client_id: clientInfo.id,
                    client_name: `${clientInfo.first_name} ${clientInfo.last_name}`,
                    client_dni: clientInfo.dni,
                    client_document_number: clientInfo.dni,
                    client_business_name: clientInfo.business_name,
                    plan_price: clientInfo.plan_price || 0,
                    balance: clientInfo.balance || 0,
                    amount: (parseFloat(clientInfo.plan_price || 0) * 2) || 0,
                    paid_amount: 0,
                    advance_paid_until: clientInfo.advance_paid_until
                  });
                  setOpenPaymentForm(true);
                }}
                sx={{ 
                  borderRadius: '8px', 
                  textTransform: 'none', 
                  fontWeight: 700, 
                  borderColor: '#0ea5e9', 
                  color: '#0ea5e9', 
                  height: '32px',
                  fontSize: '0.8125rem',
                  '&:hover': { 
                    borderColor: '#0284c7', 
                    backgroundColor: 'rgba(14, 165, 233, 0.04)' 
                  },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Registrar Pago Adelantado
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={handleClearClientFilter}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, height: '32px', fontSize: '0.8125rem', width: { xs: '100%', sm: 'auto' } }}
              >
                Ver Todas
              </Button>
            </Stack>
          }
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
              Mostrando facturas del cliente: <strong>{clientInfo.first_name} {clientInfo.last_name}</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, mt: 0.2, display: 'block', fontSize: '0.75rem' }}>
              💵 Saldo a Favor / Crédito disponible: S/ {parseFloat(clientInfo.balance || 0).toFixed(2)}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Panel Colapsable de Filtros Avanzados */}
      {showAdvancedFilters && (
        <Box sx={{ mb: 2 }}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: '16px', 
              border: '1px solid rgba(0, 0, 0, 0.05)',
              backgroundColor: 'white',
              p: 2.5
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 1 }}>
                Filtros Avanzados y Reportes
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setDateRange({ startDate: null, endDate: null });
                  setDateType('due_date');
                  setPaymentMethodFilter('');
                  setPlanFilter('');
                  setDocumentTypeFilter('');
                  setZoneFilter('');
                }}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: '#ef4444' }}
              >
                Limpiar Filtros
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: '#64748b' }}>
                    Tipo de Fecha
                  </Typography>
                  <Select
                    value={dateType}
                    onChange={(e) => setDateType(e.target.value)}
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="due_date">Fecha Vencimiento</MenuItem>
                    <MenuItem value="emission_date">Fecha Emisión</MenuItem>
                    <MenuItem value="payment_date">Fecha Pago</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: '#64748b' }}>
                    Plan de Internet
                  </Typography>
                  <Select
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="">Todos los Planes</MenuItem>
                    {plansList.map(plan => (
                      <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: '#64748b' }}>
                    Método de Pago
                  </Typography>
                  <Select
                    value={paymentMethodFilter}
                    onChange={(e) => setPaymentMethodFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="">Todos los Métodos</MenuItem>
                    {paymentMethods.map(method => (
                      <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: '#64748b' }}>
                    Tipo de Comprobante
                  </Typography>
                  <Select
                    value={documentTypeFilter}
                    onChange={(e) => setDocumentTypeFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="">Todos los Tipos</MenuItem>
                    <MenuItem value="recibo">Recibo</MenuItem>
                    <MenuItem value="boleta">Boleta</MenuItem>
                    <MenuItem value="factura">Factura</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: '#64748b' }}>
                    Zona Geográfica
                  </Typography>
                  <Select
                    value={zoneFilter}
                    onChange={(e) => setZoneFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '8px' }}
                  >
                    <MenuItem value="">Todas las Zonas</MenuItem>
                    {zonesList.map(zone => (
                      <MenuItem key={zone.id} value={zone.id}>{zone.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

            </Grid>
          </Card>
        </Box>
      )}

      {/* Panel de Estadísticas Glassmorphic */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Facturación Total"
            value={`${currencySymbol}${totalInvoiced.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<TrendingUpIcon />}
            color="#6366f1"
            subtitle="Monto total emitido"
            delay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Cobrado"
            value={`${currencySymbol}${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<CheckCircleIcon />}
            color="#10b981"
            subtitle="Monto recaudado y conciliado"
            delay={1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monto Pendiente"
            value={`${currencySymbol}${totalPending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<HourglassEmptyIcon />}
            color="#f59e0b"
            subtitle="Por cobrar del periodo"
            delay={2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Vencido"
            value={`${currencySymbol}${totalOverdue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<ErrorOutlineIcon />}
            color="#f43f5e"
            subtitle="Fuera del plazo límite"
            delay={3}
          />
        </Grid>
      </Grid>

      {/* Tabla Premium */}
      <Paper
        sx={{
          borderRadius: '24px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white',
          p: 1
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, backgroundColor: '#f8fafc' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                minWidth: { xs: 'auto', sm: 100 },
                px: { xs: 1.5, sm: 2.5 },
                py: 1,
                minHeight: 44,
                whiteSpace: 'nowrap'
              }
            }}
          >
            <Tab label={`Todas (${countAll})`} />
            <Tab label={`Pendientes (${countPending})`} />
            <Tab label={`Vencidas (${countOverdue})`} />
            <Tab label={`Pagadas (${countPaid})`} />
          </Tabs>
        </Box>
        <Box sx={{ height: 650, width: '100%' }}>
          <DataGrid
            rows={getDisplayInvoices()}
            columns={columns}
            disableRowSelectionOnClick
            checkboxSelection
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: rowsPerPage, page: page } },
            }}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
            }}
            rowSelectionModel={selectedRows}
            loading={loading}
            // autoHeight
            slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            border: 'none',
            fontFamily: 'Inter, sans-serif',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#475569',
                fontSize: '0.85rem'
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)',
              color: '#334155',
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                transform: 'scale(1.002)',
              }
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
              }
            },
          }}
        />
        </Box>
      </Paper>

      {selectedInvoice && promiseDialogOpen && (
        <PaymentPromiseDialog
          open={promiseDialogOpen}
          onClose={() => {
            setPromiseDialogOpen(false);
            setSelectedInvoice(null);
          }}
          onSubmit={handlePaymentPromise}
          invoice={selectedInvoice}
          currencySymbol={currencySymbol}
        />
      )}

      {selectedInvoice && openPaymentForm && (
        <PaymentForm
          open={openPaymentForm}
          onClose={() => {
            setOpenPaymentForm(false);
            setSelectedInvoice(null);
          }}
          onSubmit={handlePaymentSubmit}
          invoice={selectedInvoice}
          paymentMethods={paymentMethods}
          currencySymbol={currencySymbol}
        />
      )}

      {openMassPaymentForm && (
        <MassPaymentForm
          open={openMassPaymentForm}
          onClose={() => setOpenMassPaymentForm(false)}
          onSubmit={handleMassPaymentSubmit}
          invoices={invoices.filter(inv => selectedRows.includes(inv.id))}
          paymentMethods={paymentMethods}
          currencySymbol={currencySymbol}
        />
      )}

      {createInvoiceOpen && (
        <CreateInvoiceDialog
          open={createInvoiceOpen}
          onClose={() => setCreateInvoiceOpen(false)}
          onSubmit={handleCreateInvoiceSubmit}
          clients={allClients}
          currencySymbol={currencySymbol}
        />
      )}

      {successPaymentData && receiptDialogOpen && (
        <ReceiptOptionsDialog
          open={receiptDialogOpen}
          onClose={() => {
            setReceiptDialogOpen(false);
            setSuccessPaymentData(null);
          }}
          invoiceId={successPaymentData.id}
          invoiceNumber={successPaymentData.invoiceNumber}
          amount={successPaymentData.amount}
          clientEmail={successPaymentData.clientEmail}
        />
      )}
      {selectedInvoiceForDiscount && discountDialogOpen && (
        <ApplyDiscountDialog
          open={discountDialogOpen}
          onClose={() => {
            setDiscountDialogOpen(false);
            setSelectedInvoiceForDiscount(null);
          }}
          onSubmit={handleApplyDiscount}
          invoice={selectedInvoiceForDiscount}
          currencySymbol={currencySymbol}
        />
      )}
      {selectedInvoiceForEdit && editDialogOpen && (
        <EditInvoiceDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedInvoiceForEdit(null);
          }}
          onSubmit={handleEditInvoiceSubmit}
          invoice={selectedInvoiceForEdit}
          currencySymbol={currencySymbol}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setMenuInvoice(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.05)',
            mt: 0.5
          }
        }}
      >
        {menuInvoice && (
          <MenuItem
            onClick={() => {
              navigate(`/finance/invoices/${menuInvoice.id}`);
              setAnchorEl(null);
              setMenuInvoice(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <VisibilityIcon fontSize="small" sx={{ color: '#6366f1' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Ver Detalles</Typography>
          </MenuItem>
        )}

        {menuInvoice && menuInvoice.proof_image_url && (
          <MenuItem
            onClick={() => {
              setSelectedVoucher({
                url: menuInvoice.proof_image_url,
                details: {
                  method: menuInvoice.payment_method_name,
                  amount: menuInvoice.amount
                }
              });
              setVoucherModalOpen(true);
              setAnchorEl(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <ImageIcon fontSize="small" sx={{ color: '#0284c7' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Ver Captura / Voucher</Typography>
          </MenuItem>
        )}

        {menuInvoice && menuInvoice.status !== 'paid' && menuInvoice.status !== 'voided' && (
          <MenuItem
            onClick={() => {
              setSelectedInvoice(menuInvoice);
              setOpenPaymentForm(true);
              setAnchorEl(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <PaymentIcon fontSize="small" sx={{ color: '#6366f1' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Registrar Pago</Typography>
          </MenuItem>
        )}

        {menuInvoice && (menuInvoice.status === 'pending' || menuInvoice.status === 'partially_paid' || menuInvoice.status === 'overdue') && (
          <MenuItem
            onClick={() => {
              setSelectedInvoice(menuInvoice);
              setPromiseDialogOpen(true);
              setAnchorEl(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <CalendarIcon fontSize="small" sx={{ color: '#d97706' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Registrar Promesa de Pago</Typography>
          </MenuItem>
        )}

        {menuInvoice && (menuInvoice.status === 'pending' || menuInvoice.status === 'partially_paid' || menuInvoice.status === 'overdue') && (
          <MenuItem
            onClick={() => {
              setSelectedInvoiceForEdit(menuInvoice);
              setEditDialogOpen(true);
              setAnchorEl(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <EditIcon fontSize="small" sx={{ color: '#6366f1' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Editar Factura</Typography>
          </MenuItem>
        )}

        {menuInvoice && (menuInvoice.status === 'pending' || menuInvoice.status === 'partially_paid' || menuInvoice.status === 'overdue') && (
          <MenuItem
            onClick={() => {
              setSelectedInvoiceForDiscount(menuInvoice);
              setDiscountDialogOpen(true);
              setAnchorEl(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <DiscountIcon fontSize="small" sx={{ color: '#10b981' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Aplicar Descuento</Typography>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            if (menuInvoice) handleDownloadInvoice(menuInvoice.id, 'normal');
            setAnchorEl(null);
            setMenuInvoice(null);
          }}
          sx={{ gap: 1.5, py: 1 }}
        >
          <PdfIcon fontSize="small" sx={{ color: '#ef4444' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Descargar PDF (A4)</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuInvoice) handleDownloadInvoice(menuInvoice.id, 'ticket');
            setAnchorEl(null);
            setMenuInvoice(null);
          }}
          sx={{ gap: 1.5, py: 1 }}
        >
          <TicketIcon fontSize="small" sx={{ color: '#0ea5e9' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Descargar Ticket (80mm)</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuInvoice) {
              if (menuInvoice.client_email) {
                handleSendEmailDirectly(menuInvoice.id, 'ticket');
              } else {
                alert('El cliente no tiene un correo electrónico registrado.');
              }
            }
            setAnchorEl(null);
            setMenuInvoice(null);
          }}
          sx={{ gap: 1.5, py: 1 }}
        >
          <EmailIcon fontSize="small" sx={{ color: '#6366f1' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Enviar Ticket por Correo</Typography>
        </MenuItem>

        {menuInvoice && menuInvoice.sunat_pdf_url && (
          <MenuItem
            onClick={() => {
              handleOpenPdfModal(menuInvoice.sunat_pdf_url, menuInvoice.sunat_number);
              setAnchorEl(null);
              setMenuInvoice(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <ReceiptIcon fontSize="small" sx={{ color: '#059669' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#047857' }}>Ver Boleta SUNAT (PDF)</Typography>
          </MenuItem>
        )}

        {menuInvoice && menuInvoice.status === 'paid' && menuInvoice.sunat_status !== 'ACEPTADO' && (
          <MenuItem
            onClick={() => {
              const invId = menuInvoice.id;
              setAnchorEl(null);
              setMenuInvoice(null);
              handleEmitBoletaSunat(invId);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <SendIcon fontSize="small" sx={{ color: '#059669' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>Emitir Boleta SUNAT</Typography>
          </MenuItem>
        )}

        {menuInvoice && (user?.role === 'admin' || hasPermission('roles.manage')) && (
          <MenuItem
            onClick={() => {
              handleDeleteInvoice(menuInvoice.id);
              setAnchorEl(null);
              setMenuInvoice(null);
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#ef4444' }}>Eliminar Factura</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Modal para Visualizar Captura / Voucher de Pago */}
      <Dialog
        open={voucherModalOpen}
        onClose={() => setVoucherModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon sx={{ color: '#0284c7' }} />
            <Typography variant="h6" fontWeight={700}>
              Captura de Comprobante / Voucher
            </Typography>
          </Box>
          <IconButton onClick={() => setVoucherModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ textAlign: 'center', py: 3, bgcolor: '#f8fafc' }}>
          {selectedVoucher ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e2e8f0', p: 1, bgcolor: '#ffffff' }}>
                <img
                  src={selectedVoucher.url}
                  alt="Voucher de Pago"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '65vh',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              </Paper>
              {selectedVoucher.details && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
                  {selectedVoucher.details.method && (
                    <Chip label={`Método: ${selectedVoucher.details.method}`} color="primary" variant="outlined" size="small" />
                  )}
                  {selectedVoucher.details.reference && (
                    <Chip label={`Ref: ${selectedVoucher.details.reference}`} color="secondary" variant="outlined" size="small" />
                  )}
                  {selectedVoucher.details.amount && (
                    <Chip label={`Monto: S/ ${parseFloat(selectedVoucher.details.amount).toFixed(2)}`} color="success" size="small" />
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">No se encontró la imagen del comprobante.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
          {selectedVoucher?.url ? (
            <Button
              component="a"
              href={selectedVoucher.url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
              size="small"
            >
              Abrir Imagen Completa
            </Button>
          ) : <div />}
          <Button variant="contained" onClick={() => setVoucherModalOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para Visualizar Comprobante SUNAT PDF (A4 vs Ticket) */}
      <Dialog
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            height: '90vh',
            maxHeight: '850px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            px: 3,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptIcon sx={{ color: '#34d399', fontSize: 26 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2, color: '#f8fafc' }}>
                Comprobante Electrónico SUNAT
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                {pdfViewerData.sunatNumber || 'Boleta de Venta Electrónica'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ToggleButtonGroup
              value={pdfViewerData.currentFormat}
              exclusive
              onChange={(e, newFormat) => {
                if (newFormat) {
                  setPdfViewerData(prev => ({ ...prev, currentFormat: newFormat }));
                }
              }}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 0.5,
                '& .MuiToggleButton-root': {
                  color: '#94a3b8',
                  border: 'none',
                  borderRadius: 1.5,
                  px: 2,
                  py: 0.5,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.2s',
                  '&.Mui-selected': {
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#059669'
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff'
                  }
                }
              }}
            >
              <ToggleButton value="a4">
                📄 Formato A4
              </ToggleButton>
              <ToggleButton value="ticket">
                🎟️ Formato Ticket
              </ToggleButton>
            </ToggleButtonGroup>

            <Tooltip title="Abrir en pestaña nueva">
              <IconButton
                onClick={() => {
                  const currentUrl = pdfViewerData.currentFormat === 'ticket' 
                    ? pdfViewerData.pdfUrlTicket 
                    : pdfViewerData.pdfUrlA4;
                  window.open(currentUrl, '_blank');
                }}
                sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <IconButton
              onClick={() => setPdfViewerOpen(false)}
              sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: '#f8fafc', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <iframe
            src={
              pdfViewerData.currentFormat === 'ticket' 
                ? pdfViewerData.pdfUrlTicket 
                : pdfViewerData.pdfUrlA4
            }
            title="Comprobante SUNAT PDF"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              flexGrow: 1
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;